import { GridColumnProps, SelectOption } from '@roc/ui';
import { dateFilterProps, filterProps, noOpComparator, selectFilterProps } from '@roc/ui/utils';
import { PaymentApprovalFilter } from '../types/types';
import { insertIf } from '@roc/feature-utils';

const LOAN_PROPERTY_TAXES_STATUS_OPTIONS: Array<SelectOption> = [
  { label: 'Order Created', value: 'Order Created' },
  { label: 'Certification Received', value: 'Certification Received' },
  { label: 'Redemption Requested', value: 'Redemption Requested' },
  { label: 'Pending Payment Approval', value: 'Pending Payment Approval' },
  { label: 'Payment Requested', value: 'Payment Requested' },
  { label: 'Payment Processed', value: 'Payment Processed' },
  { label: 'Paid Off', value: 'Paid Off' },
];

const PROPERTY_TAXES_STATUS_OPTIONS: Array<SelectOption> = [
  { label: 'PENDING', value: 'PE' },
  { label: 'REJECTED', value: 'RJ' },
  { label: 'DECLINED PAYMENT', value: 'DP' },
  { label: 'APPROVED FOR PAYMENT', value: 'PY' },
  { label: 'PROCESSING PAYMENT', value: 'PP' },
  { label: 'PAYMENT COMPLETED', value: 'PC' },
];

export const TAX_CERT_COLUMNS: GridColumnProps[] = [
  {
    field: 'loanId',
    headerName: 'Loan Id',
    width: 90,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'orderId',
    headerName: 'Order Id',
    width: 90,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 150,
    comparator: noOpComparator,
    cellRenderer: 'taxCertificateStatusCellRenderer',
    ...selectFilterProps(LOAN_PROPERTY_TAXES_STATUS_OPTIONS),
  },
  {
    field: 'createdDate',
    headerName: 'Boarded Date',
    cellRenderer: 'dateCellRenderer',
    minWidth: 125,
    comparator: noOpComparator,
    ...dateFilterProps(),
  },
  {
    field: 'amountDue',
    headerName: 'Due Amount',
    minWidth: 90,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'delinquentAmount',
    headerName: 'Delinquent Amount',
    minWidth: 90,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'goodThroughDate',
    headerName: 'Good Through Date',
    cellRenderer: 'dateCellRenderer',
    minWidth: 125,
    comparator: noOpComparator,
    ...dateFilterProps(),
  },
  {
    field: 'originalDueDate',
    headerName: 'Original Due Date',
    cellRenderer: 'dateCellRenderer',
    minWidth: 125,
    comparator: noOpComparator,
    ...dateFilterProps(),
  },
  {
    field: '',
    headerName: 'Action',
    minWidth: 80,
    cellRenderer: 'actionCellRenderer',
    comparator: noOpComparator,
    width: 80,
    pinned: 'right',
    lockPinned: true,
  },
];

export const getPaymentApprovalColumns = (filter: PaymentApprovalFilter): GridColumnProps[] => {
  return [
    {
      field: 'loanId',
      headerName: 'Loan Id',
      width: 90,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 125,
      comparator: noOpComparator,
      ...selectFilterProps(PROPERTY_TAXES_STATUS_OPTIONS),
      editable: filter != PaymentApprovalFilter.ALL,
      cellEditor: filter != PaymentApprovalFilter.ALL && 'dropdownGridEditor',
    },
    {
      field: 'parcel',
      headerName: 'Parcel',
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'jurisdiction',
      headerName: 'Jurisdiction',
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'taxYear',
      headerName: 'Tax Year',
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'tape',
      headerName: 'Tape',
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps(),
      cellRenderer: 'linkColumnCellRenderer',
      hide: filter === PaymentApprovalFilter.TAPE,
    },
    {
      field: 'receivableStatus',
      headerName: 'Receivable Status',
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps(),
      hide: filter != PaymentApprovalFilter.TAPE,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      minWidth: 125,
      cellRenderer: 'currencyCellRenderer',
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'goodThroughDate',
      headerName: 'Good Through Date',
      cellRenderer: 'dateCellRenderer',
      minWidth: 125,
      comparator: noOpComparator,
      ...dateFilterProps(),
    },
    {
      field: 'originalDueDate',
      headerName: 'Original Due Date',
      cellRenderer: 'dateCellRenderer',
      minWidth: 125,
      comparator: noOpComparator,
      ...dateFilterProps(),
    },
  ];
}

export const TAX_REDEMPTION_COLUMNS: GridColumnProps[] = [
  {
    field: 'loanId',
    headerName: 'Loan Id',
    width: 90,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 125,
    comparator: noOpComparator,
    ...selectFilterProps(LOAN_PROPERTY_TAXES_STATUS_OPTIONS),
  },
  {
    field: 'amountDue',
    headerName: 'Due Amount',
    minWidth: 90,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'delinquentAmount',
    headerName: 'Delinquent Amount',
    minWidth: 90,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'maturityDate',
    headerName: 'Maturity Date',
    cellRenderer: 'dateCellRenderer',
    minWidth: 125,
    comparator: noOpComparator,
    ...dateFilterProps(),
  },
  {
    field: 'foreclosureStartDate',
    headerName: 'Foreclosure Date',
    cellRenderer: 'dateCellRenderer',
    minWidth: 125,
    comparator: noOpComparator,
    ...dateFilterProps(),
  },
  {
    field: 'reoDate',
    headerName: 'REO Date',
    cellRenderer: 'dateCellRenderer',
    minWidth: 125,
    comparator: noOpComparator,
    ...dateFilterProps(),
  },
  {
    field: 'dqStatus',
    headerName: 'DQ Status',
    minWidth: 125,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: '',
    headerName: 'Action',
    minWidth: 80,
    cellRenderer: 'actionCellRenderer',
    comparator: noOpComparator,
    width: 80,
    pinned: 'right',
    lockPinned: true,
  },
];

export const PAYMENT_APPROVAL_FILTER: Array<SelectOption> = [
  { label: 'Pending', value: PaymentApprovalFilter.PENDING },
  { label: 'All', value: PaymentApprovalFilter.ALL },
];
